import Avatar from "@mui/material/Avatar";
import { teal } from "@mui/material/colors";
import { styled } from "@mui/material/styles";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { MAX_VISIBLE_TEAM_MEMBERS } from "_shared/teams/team-members-display";
import { MarketplaceIndividualInfoJSON } from "marketplace/_schema/marketplace-individual-info-schema";
import { MoreCount } from "marketplace/_views/marketplace-result-card";
import { getResultInitials, getText } from "marketplace/_views/result-text";
import React from "react";

const Roster = styled("div")(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(2),
  gridAutoFlow: "row",
  minWidth: 0,
}));

const MemberName = styled("div")(() => ({
  minWidth: 0,
}));

const Faces = styled("span")(() => ({
  display: "inline-flex",
  flexShrink: 0,
}));

const Face = styled(Avatar)(({ theme }) => ({
  backgroundColor: teal[50],
  border: `2px solid ${theme.palette.background.paper}`,
  boxSizing: "content-box",
  color: teal[800],
  fontSize: 10,
  fontWeight: theme.typography.fontWeightMedium,
  height: 22,
  width: 22,
  "&:not(:first-of-type)": {
    marginLeft: -6,
  },
}));

const ViewAllButton = styled("button")(({ theme }) => ({
  background: "none",
  border: 0,
  color: theme.palette.primary.main,
  cursor: "pointer",
  font: "inherit",
  padding: 0,
  textDecoration: "underline",
  whiteSpace: "nowrap",
}));

type TeamResultMembersProps = {
  members?: MarketplaceIndividualInfoJSON[] | null;
  /** The whole roster rather than the card's summary of it: what "View all" opens. */
  showAllMembers?: boolean;
  renderMember: (member: MarketplaceIndividualInfoJSON) => React.ReactNode;
};

/**
 * A team card's members, on one line: up to three faces, "+N" for the rest, and "View all", which
 * opens the whole roster with each member's profile link. The faces are named for a screen reader and
 * a tooltip; the card fetches no pictures for them, so they are always initials.
 */
export default function TeamResultMembers(props: Readonly<TeamResultMembersProps>) {
  const { members, renderMember } = props;
  const showAllMembers = props.showAllMembers ?? false;
  const { openDialog, closeDialog } = useDialog();
  const roster = members ?? [];

  if (showAllMembers) {
    return (
      <Roster>
        {roster.map((member, index) => (
          <MemberName key={member.id ?? member.userId ?? index}>{renderMember(member)}</MemberName>
        ))}
      </Roster>
    );
  }

  if (roster.length === 0) return null;

  const shown = roster.slice(0, MAX_VISIBLE_TEAM_MEMBERS);
  const hiddenCount = roster.length - shown.length;

  return (
    <>
      <Faces>
        {shown.map((member, index) => {
          const name = getText(["firstName", "lastName"], member, true);
          return (
            <Face key={member.id ?? member.userId ?? index} role="img" aria-label={name} title={name}>
              {getResultInitials(name)}
            </Face>
          );
        })}
      </Faces>
      {hiddenCount > 0 && <MoreCount role="img" aria-label={`${hiddenCount} more`}>+{hiddenCount}</MoreCount>}
      <ViewAllButton
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          openDialog({
            title: `Team members (${roster.length})`,
            component: <TeamResultMembers members={roster} showAllMembers renderMember={renderMember} />,
            MuiProps: {
              fullWidth: true,
              maxWidth: "sm",
            },
            onClose: closeDialog,
          });
        }}
      >
        View all
      </ViewAllButton>
    </>
  );
}
